import { State } from "..";

import { AbstractStore } from ".";

export type TypeReleaseNotes = {
  /**
   * ID of the last seen changelog
   */
  lastSeenId: string;

  /**
   * Timestamp of the last seen changelog (unused for now)
   */
  lastSeenAt: string;

  /**
   * Server ids for which the over-capacity "pay-or-purge" notice has already
   * been shown for the current over-capacity episode. Entries are cleared once
   * a server is no longer over capacity, so a future breach shows again.
   *
   * Reuses the same seen/dismissed throttling approach as the changelog so the
   * manager notice is shown on next open rather than on every load.
   */
  seenOverCapacity: Record<string, boolean>;
};

export class ReleaseNotes extends AbstractStore<
  "release-notes",
  TypeReleaseNotes
> {
  constructor(state: State) {
    super(state, "release-notes");
  }

  get() {
    return super.get();
  }

  hydrate(): void {}

  default(): TypeReleaseNotes {
    return {
      lastSeenId: "",
      lastSeenAt: "",
      seenOverCapacity: {},
    };
  }

  clean(input: Partial<TypeReleaseNotes>): TypeReleaseNotes {
    const seenOverCapacity: Record<string, boolean> = {};
    if (input.seenOverCapacity && typeof input.seenOverCapacity === "object") {
      for (const key of Object.keys(input.seenOverCapacity)) {
        if (input.seenOverCapacity[key] === true) {
          seenOverCapacity[key] = true;
        }
      }
    }

    return {
      lastSeenId: typeof input.lastSeenId === "string" ? input.lastSeenId : "",
      lastSeenAt: typeof input.lastSeenAt === "string" ? input.lastSeenAt : "",
      seenOverCapacity,
    };
  }

  get lastSeenId(): string {
    return this.get().lastSeenId;
  }

  markSeen(id: string, publishedAt: string): void {
    this.set("lastSeenId", id);
    this.set("lastSeenAt", publishedAt);
  }

  /**
   * Whether the over-capacity notice has already been shown for this server
   * during the current over-capacity episode.
   */
  hasSeenOverCapacity(serverId: string): boolean {
    return this.get().seenOverCapacity[serverId] === true;
  }

  /**
   * Mark the over-capacity notice as seen for a server.
   */
  markOverCapacitySeen(serverId: string): void {
    this.set("seenOverCapacity", serverId, true);
  }

  /**
   * Clear the seen flag for a server (called once it is no longer over
   * capacity) so a future breach shows the notice again.
   */
  clearOverCapacitySeen(serverId: string): void {
    if (this.get().seenOverCapacity[serverId]) {
      this.set("seenOverCapacity", serverId, false);
    }
  }
}
